import React, { useState, useEffect } from 'react';
import {
  signInWithEmailAndPassword,
  signInWithPopup,
  signInWithRedirect,
  getRedirectResult,
  sendPasswordResetEmail,
} from 'firebase/auth';
import { auth, googleProvider } from '../firebase.js';

const isMobileSafari = /iPhone|iPad|iPod/i.test(navigator.userAgent);

// view: 'login' | 'forgot' | 'sent'
export default function LoginScreen() {
  const [view, setView]         = useState('login');
  const [email, setEmail]       = useState('');
  const [password, setPassword] = useState('');
  const [resetEmail, setResetEmail] = useState('');
  const [sentTo, setSentTo]     = useState('');
  const [loading, setLoading]   = useState(false);
  const [error, setError]       = useState(null);

  // Handle iOS Safari Google redirect return
  useEffect(() => {
    getRedirectResult(auth).catch((err) => {
      console.error("getRedirectResult error:", err);
      // Only set error if it's an actual failure, not just a missing redirect state
      if (err.code !== 'auth/redirect-cancelled-by-user') {
        setError(`Google Auth Redirect Error: ${err.message}`);
      }
    });
  }, []);

  const handleEmailSignIn = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    try {
      await signInWithEmailAndPassword(auth, email, password);
      // onAuthStateChanged in App.jsx handles navigation
    } catch {
      setError('Incorrect email or password.');
      setLoading(false);
    }
  };

  const handleGoogleSignIn = async () => {
    setLoading(true);
    setError(null);
    try {
      if (isMobileSafari) {
        await signInWithRedirect(auth, googleProvider);
        // redirect — page will reload; getRedirectResult handles return
      } else {
        await signInWithPopup(auth, googleProvider);
      }
    } catch (err) {
      console.error("handleGoogleSignIn error:", err);
      setError(`Google sign-in failed: ${err.message}`);
      setLoading(false);
    }
  };

  const handleSendReset = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await sendPasswordResetEmail(auth, resetEmail);
    } catch {
      // intentional no-op — always show generic confirmation
    }
    setSentTo(resetEmail);
    setView('sent');
    setLoading(false);
  };

  return (
    <div style={styles.page}>
      <div style={styles.bg} />

      <div style={styles.card}>
        <div style={styles.brand}>
          <div style={styles.orb}>🌿</div>
          <div style={styles.brandName}>Tish</div>
          <div style={styles.brandSub}>Your AI Chief of Staff</div>
        </div>

        {view === 'login' && (
          <form onSubmit={handleEmailSignIn} noValidate>
            <div style={styles.field}>
              <label style={styles.label} htmlFor="login-email">Email</label>
              <input
                id="login-email"
                type="email"
                autoComplete="email"
                value={email}
                onChange={e => setEmail(e.target.value)}
                style={{ ...styles.input, ...(error ? styles.inputError : {}) }}
                aria-invalid={error ? 'true' : 'false'}
                aria-describedby={error ? 'login-error' : undefined}
                disabled={loading}
                placeholder="you@example.com"
              />
            </div>

            <div style={styles.field}>
              <div style={styles.fieldHeader}>
                <label style={styles.label} htmlFor="login-password">Password</label>
                <button
                  type="button"
                  onClick={() => { setView('forgot'); setResetEmail(email); setError(null); }}
                  style={styles.forgotLink}
                  aria-label="Forgot your password?"
                >
                  Forgot password?
                </button>
              </div>
              <input
                id="login-password"
                type="password"
                autoComplete="current-password"
                value={password}
                onChange={e => setPassword(e.target.value)}
                style={{ ...styles.input, ...(error ? styles.inputError : {}) }}
                aria-invalid={error ? 'true' : 'false'}
                disabled={loading}
                placeholder="••••••••"
              />
            </div>

            {error && (
              <div id="login-error" role="alert" aria-live="assertive" style={styles.errorBanner}>
                {error}
              </div>
            )}

            <button
              type="submit"
              disabled={loading || !email || !password}
              aria-busy={loading}
              style={{ ...styles.btnPrimary, ...(loading ? styles.btnDisabled : {}) }}
            >
              {loading ? <Spinner /> : 'Sign In'}
            </button>

            <div style={styles.divider}>
              <div style={styles.dividerLine} />
              <span style={styles.dividerText}>or</span>
              <div style={styles.dividerLine} />
            </div>

            <button
              type="button"
              onClick={handleGoogleSignIn}
              disabled={loading}
              aria-busy={loading}
              style={{ ...styles.btnGoogle, ...(loading ? styles.btnDisabled : {}) }}
            >
              <GoogleIcon />
              Continue with Google
            </button>
          </form>
        )}

        {view === 'forgot' && (
          <form onSubmit={handleSendReset} noValidate>
            <p style={styles.forgotPrompt}>
              Enter your email and we'll send a password reset link.
            </p>
            <div style={styles.field}>
              <label style={styles.label} htmlFor="reset-email">Email</label>
              <input
                id="reset-email"
                type="email"
                autoComplete="email"
                value={resetEmail}
                onChange={e => setResetEmail(e.target.value)}
                style={styles.input}
                disabled={loading}
                placeholder="you@example.com"
              />
            </div>
            <button
              type="submit"
              disabled={loading || !resetEmail}
              aria-busy={loading}
              style={{ ...styles.btnPrimary, ...(loading ? styles.btnDisabled : {}) }}
            >
              {loading ? <Spinner /> : 'Send Reset Link'}
            </button>
            <button
              type="button"
              onClick={() => { setView('login'); setError(null); }}
              style={styles.backLink}
            >
              ← Back to Sign In
            </button>
          </form>
        )}

        {view === 'sent' && (
          <div role="status" aria-live="polite" style={styles.sentBox}>
            <div style={styles.sentIcon}>✉️</div>
            <p style={styles.sentTitle}>Check your inbox</p>
            <p style={styles.sentBody}>
              If <strong>{sentTo}</strong> is registered, a reset link has been sent.
              The link expires in 1 hour.
            </p>
            <p style={styles.sentHint}>
              Don't see it? Check your spam folder.
            </p>
            <button
              type="button"
              onClick={() => { setView('login'); setError(null); }}
              style={styles.backLink}
            >
              ← Back to Sign In
            </button>
          </div>
        )}
      </div>

      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
    </div>
  );
}

function Spinner() {
  return (
    <div style={{
      width: 16, height: 16,
      border: '2px solid rgba(255,255,255,0.3)',
      borderTopColor: '#fff',
      borderRadius: '50%',
      animation: 'spin 0.7s linear infinite',
      display: 'inline-block',
    }} />
  );
}

function GoogleIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 18 18" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
      <path d="M17.64 9.2c0-.637-.057-1.251-.164-1.84H9v3.481h4.844c-.209 1.125-.843 2.078-1.796 2.717v2.258h2.908c1.702-1.567 2.684-3.874 2.684-6.615z" fill="#4285F4"/>
      <path d="M9 18c2.43 0 4.467-.806 5.956-2.18l-2.908-2.259c-.806.54-1.837.86-3.048.86-2.344 0-4.328-1.584-5.036-3.711H.957v2.332A8.997 8.997 0 0 0 9 18z" fill="#34A853"/>
      <path d="M3.964 10.71A5.41 5.41 0 0 1 3.682 9c0-.593.102-1.17.282-1.71V4.958H.957A8.996 8.996 0 0 0 0 9c0 1.452.348 2.827.957 4.042l3.007-2.332z" fill="#FBBC05"/>
      <path d="M9 3.58c1.321 0 2.508.454 3.44 1.345l2.582-2.58C13.463.891 11.426 0 9 0A8.997 8.997 0 0 0 .957 4.958L3.964 7.29C4.672 5.163 6.656 3.58 9 3.58z" fill="#EA4335"/>
    </svg>
  );
}

const styles = {
  page: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    minHeight: '100vh',
    background: '#080e14',
    padding: '24px',
    position: 'relative',
  },
  bg: {
    position: 'fixed', inset: 0,
    background: 'radial-gradient(ellipse at 30% 20%, rgba(27,58,44,0.45) 0%, transparent 60%), radial-gradient(ellipse at 70% 80%, rgba(16,36,28,0.35) 0%, transparent 55%), #080e14',
    zIndex: 0,
  },
  card: {
    position: 'relative', zIndex: 1,
    width: '100%', maxWidth: '420px',
    background: 'rgba(255,255,255,0.04)',
    backdropFilter: 'blur(20px)',
    border: '1px solid rgba(255,255,255,0.08)',
    borderRadius: '20px',
    padding: '48px 40px 40px',
    boxShadow: '0 32px 64px rgba(0,0,0,0.5)',
  },
  brand: { textAlign: 'center', marginBottom: '32px' },
  orb: {
    width: 52, height: 52, borderRadius: '50%',
    background: 'linear-gradient(135deg, #2d6a4f 0%, #1b3a2c 100%)',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    margin: '0 auto 12px', fontSize: 22,
    boxShadow: '0 0 0 1px rgba(255,255,255,0.08), 0 8px 24px rgba(0,0,0,0.4)',
  },
  brandName: { fontSize: 20, fontWeight: 800, color: '#ffffff', letterSpacing: '-0.3px' },
  brandSub: { fontSize: 13, color: 'rgba(255,255,255,0.45)', marginTop: 4 },
  field: { marginBottom: 16 },
  fieldHeader: { display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: 6 },
  label: { fontSize: 12, fontWeight: 600, color: 'rgba(255,255,255,0.55)', letterSpacing: '0.4px', textTransform: 'uppercase' },
  forgotLink: {
    fontSize: 12, color: 'rgba(45,106,79,0.9)', fontWeight: 500,
    background: 'none', border: 'none', cursor: 'pointer', padding: 0,
  },
  input: {
    width: '100%', height: 48,
    background: 'rgba(255,255,255,0.06)',
    border: '1px solid rgba(255,255,255,0.1)',
    borderRadius: 12, padding: '0 16px',
    fontSize: 15, color: '#ffffff', outline: 'none',
    boxSizing: 'border-box',
  },
  inputError: { borderColor: 'rgba(239,68,68,0.5)' },
  errorBanner: {
    marginBottom: 12,
    padding: '10px 14px',
    background: 'rgba(239,68,68,0.08)',
    border: '1px solid rgba(239,68,68,0.2)',
    borderRadius: 10,
    color: '#fca5a5',
    fontSize: 13,
    textAlign: 'center',
  },
  btnPrimary: {
    width: '100%', height: 48,
    background: 'linear-gradient(135deg, #2d6a4f 0%, #1b3a2c 100%)',
    border: 'none', borderRadius: 12,
    color: '#ffffff', fontSize: 15, fontWeight: 700,
    cursor: 'pointer', marginTop: 8,
    display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 10,
    boxSizing: 'border-box',
  },
  btnGoogle: {
    width: '100%', height: 48,
    background: 'rgba(255,255,255,0.06)',
    border: '1px solid rgba(255,255,255,0.12)',
    borderRadius: 12,
    color: '#ffffff', fontSize: 14, fontWeight: 600,
    cursor: 'pointer',
    display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 10,
    boxSizing: 'border-box',
  },
  btnDisabled: { opacity: 0.5, cursor: 'not-allowed' },
  divider: { display: 'flex', alignItems: 'center', gap: 12, margin: '20px 0' },
  dividerLine: { flex: 1, height: 1, background: 'rgba(255,255,255,0.08)' },
  dividerText: { fontSize: 12, color: 'rgba(255,255,255,0.3)', fontWeight: 500 },
  forgotPrompt: { fontSize: 13, color: 'rgba(255,255,255,0.55)', marginBottom: 20, lineHeight: 1.5 },
  backLink: {
    display: 'block', width: '100%', marginTop: 16,
    background: 'none', border: 'none', cursor: 'pointer',
    fontSize: 13, color: 'rgba(255,255,255,0.4)',
    textAlign: 'center', padding: '8px 0',
  },
  sentBox: { textAlign: 'center' },
  sentIcon: { fontSize: 40, marginBottom: 16 },
  sentTitle: { fontSize: 18, fontWeight: 700, color: '#ffffff', marginBottom: 12 },
  sentBody: { fontSize: 14, color: 'rgba(255,255,255,0.6)', lineHeight: 1.6, marginBottom: 8 },
  sentHint: { fontSize: 12, color: 'rgba(255,255,255,0.35)', marginBottom: 20 },
};
